import { PAYMENT_METHOD_BY_CODE, PAYMENT_OBJECT_BY_CODE } from '../../sale/model/fiscalCodes'
import { isVatCode, type VatCode } from '../../sale/model/vat'
import { type Cart, type CartLine } from '../../sale/model/cart'
import { type ReceiptRequestDraft } from '../../sale/model/draft'
import { type SaleProfile } from '../../sale/model/profile'

/**
 * Снимок строк чека на начало операции (RP-OWN4, CASH-CONTRACTS §2.2 п. 8). Фиксируется, когда кассир нажал «Оплатить» (а не при
 * повторе): профиль карточки, цена, ставка, версия карточки и источник, плюс готовый запрос агенту. Повтор и восстановление берут
 * его, поэтому правка карточки или прайса после начала операции их не меняет. Хранится в реестре хранения со сроком `session`
 * (переживает F5) — это намерение человека («вот что я пробивал»), а не копия ответа службы.
 */

export type OwnSnapshotPayment = 'Cash' | 'Card'

export interface OwnSnapshotLine {
  productKey: string
  name: string
  priceKopecks: number
  quantityMilli: number
  vat: VatCode
  unit: string | null
  isWeight: boolean
  profile: SaleProfile
}

export interface OwnSaleSnapshot {
  startedAt: string
  paymentKind: OwnSnapshotPayment
  totalKopecks: number
  lines: readonly OwnSnapshotLine[]
  /** Запрос агенту без ключа операции: его выдаёт контроллер сессии (тот же запрос — тот же ключ). */
  draft: ReceiptRequestDraft
}

function lineOf(line: CartLine): OwnSnapshotLine | null {
  if (line.priceKopecks === null || line.vat === null || line.profile === null) return null
  return {
    productKey: line.productKey,
    name: line.name,
    priceKopecks: line.priceKopecks,
    quantityMilli: line.quantityMilli,
    vat: line.vat,
    unit: line.unit,
    isWeight: line.isWeight,
    profile: line.profile,
  }
}

/** Снимок из корзины и собранного запроса; `null`, если в корзине есть строка без цены, ставки или профиля (такую оплату не начинают). */
export function takeSnapshot(input: { cart: Cart; draft: ReceiptRequestDraft; paymentKind: OwnSnapshotPayment; totalKopecks: number; startedAt: string }): OwnSaleSnapshot | null {
  const lines = input.cart.lines.map(lineOf)
  const complete = lines.filter((line): line is OwnSnapshotLine => line !== null)
  if (complete.length === 0 || complete.length !== lines.length) return null
  return { startedAt: input.startedAt, paymentKind: input.paymentKind, totalKopecks: input.totalKopecks, lines: complete, draft: input.draft }
}

/** Корзина из снимка: восстановление после F5 показывает ровно то, что было начато, а не то, что в карточках сейчас. */
export function cartOfSnapshot(snapshot: OwnSaleSnapshot): Cart {
  return {
    lines: snapshot.lines.map((line, index) => ({
      id: index + 1,
      productKey: line.productKey,
      name: line.name,
      sku: null,
      barcode: null,
      priceKopecks: line.priceKopecks,
      quantityMilli: line.quantityMilli,
      vat: line.vat,
      unit: line.unit,
      isWeight: line.isWeight,
      profile: line.profile,
    })),
    nextId: snapshot.lines.length + 1,
  }
}

/** Та же ли это продажа: товары, цены, ставки, количества, способ оплаты и итог совпали. Только тогда повтор берёт снимок. */
export function snapshotMatches(snapshot: OwnSaleSnapshot, cart: Cart, paymentKind: OwnSnapshotPayment, totalKopecks: number): boolean {
  if (snapshot.paymentKind !== paymentKind || snapshot.totalKopecks !== totalKopecks || snapshot.lines.length !== cart.lines.length) return false
  return snapshot.lines.every((line, index) => {
    const other = cart.lines[index]
    return (
      other !== undefined &&
      line.productKey === other.productKey &&
      line.priceKopecks === other.priceKopecks &&
      line.quantityMilli === other.quantityMilli &&
      line.vat === other.vat
    )
  })
}

// ——— разбор сохранённого (D-072): повреждённое не читается, а не угадывается ———

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)
const isText = (value: unknown): value is string => typeof value === 'string'
const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)
const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
const isBool = (value: unknown): value is boolean => typeof value === 'boolean'
const nullable = <T>(value: unknown, check: (value: unknown) => value is T): value is T | null => value === null || check(value)
const objects = new Set<string>(Object.values(PAYMENT_OBJECT_BY_CODE))
const methods = new Set<string>(Object.values(PAYMENT_METHOD_BY_CODE))
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const PAYMENT_TYPES: readonly string[] = ['cash', 'electronically', 'card']

function decodeProfile(raw: unknown): SaleProfile | undefined {
  if (!isRecord(raw)) return undefined
  const { paymentSubject, paymentMethod, isMarked, isWeighted, unitCode, unitName, cardVersion, origin } = raw
  if (!nullable(paymentSubject, isCount) || !nullable(paymentMethod, isCount) || !nullable(isMarked, isBool) || !nullable(isWeighted, isBool)) return undefined
  if (!nullable(unitCode, isText) || !nullable(unitName, isText) || !nullable(cardVersion, isCount)) return undefined
  let decodedOrigin: SaleProfile['origin'] = null
  if (origin !== null) {
    if (!isRecord(origin) || !isText(origin.resourceId) || !isText(origin.key) || origin.kind !== 'own') return undefined
    decodedOrigin = { resourceId: origin.resourceId, kind: origin.kind, key: origin.key }
  }
  return { paymentSubject, paymentMethod, isMarked, isWeighted, unitCode, unitName, cardVersion, origin: decodedOrigin }
}

function decodeLine(raw: unknown): OwnSnapshotLine | undefined {
  if (!isRecord(raw)) return undefined
  const { productKey, name, priceKopecks, quantityMilli, vat, unit, isWeight } = raw
  if (!isText(productKey) || productKey === '' || !isText(name) || !isCount(priceKopecks) || !isCount(quantityMilli) || quantityMilli === 0) return undefined
  if (!isVatCode(vat) || !nullable(unit, isText) || !isBool(isWeight)) return undefined
  const profile = decodeProfile(raw.profile)
  return profile === undefined ? undefined : { productKey, name, priceKopecks, quantityMilli, vat, unit, isWeight, profile }
}

function decodeDraft(raw: unknown): ReceiptRequestDraft | undefined {
  if (!isRecord(raw) || (raw.type !== 'sale' && raw.type !== 'refund') || !isRecord(raw.cashier) || !isText(raw.cashier.name)) return undefined
  if (!Array.isArray(raw.items) || raw.items.length === 0 || !Array.isArray(raw.payments) || raw.payments.length === 0) return undefined
  const items: ReceiptRequestDraft['items'][number][] = []
  for (const entry of raw.items as unknown[]) {
    if (!isRecord(entry) || !isText(entry.name) || !isNumber(entry.price) || !isNumber(entry.quantity) || !isNumber(entry.amount)) return undefined
    if (!isVatCode(entry.vat) || !isText(entry.paymentObject) || !objects.has(entry.paymentObject) || !isText(entry.paymentMethod) || !methods.has(entry.paymentMethod)) return undefined
    if (entry.markingCode !== null) return undefined
    items.push({ name: entry.name, price: entry.price, quantity: entry.quantity, amount: entry.amount, vat: entry.vat, paymentObject: entry.paymentObject, paymentMethod: entry.paymentMethod, markingCode: null })
  }
  const payments: ReceiptRequestDraft['payments'][number][] = []
  for (const entry of raw.payments as unknown[]) {
    if (!isRecord(entry) || !isText(entry.type) || !PAYMENT_TYPES.includes(entry.type) || !isNumber(entry.amount)) return undefined
    payments.push({ type: entry.type as 'cash' | 'electronically' | 'card', amount: entry.amount })
  }
  const customer = raw.customer
  if (customer !== undefined && (!isRecord(customer) || !isText(customer.emailOrPhone))) return undefined
  if (raw.print !== undefined && raw.print !== 'print' && raw.print !== 'electronic') return undefined
  return {
    type: raw.type,
    cashier: {
      name: raw.cashier.name,
      ...(isText(raw.cashier.inn) ? { inn: raw.cashier.inn } : {}),
      ...(isText(raw.cashier.cashierId) && GUID.test(raw.cashier.cashierId) ? { cashierId: raw.cashier.cashierId } : {}),
    },
    items,
    payments,
    ...(customer === undefined ? {} : { customer: { emailOrPhone: (customer as { emailOrPhone: string }).emailOrPhone } }),
    ...(raw.print === undefined ? {} : { print: raw.print }),
  }
}

export function decodeSnapshot(raw: unknown): OwnSaleSnapshot | undefined {
  if (!isRecord(raw) || !isText(raw.startedAt) || Number.isNaN(Date.parse(raw.startedAt)) || (raw.paymentKind !== 'Cash' && raw.paymentKind !== 'Card') || !isCount(raw.totalKopecks)) return undefined
  if (!Array.isArray(raw.lines) || raw.lines.length === 0) return undefined
  const lines = (raw.lines as unknown[]).map(decodeLine)
  const complete = lines.filter((line): line is OwnSnapshotLine => line !== undefined)
  if (complete.length !== lines.length) return undefined
  const draft = decodeDraft(raw.draft)
  return draft === undefined ? undefined : { startedAt: raw.startedAt, paymentKind: raw.paymentKind, totalKopecks: raw.totalKopecks, lines: complete, draft }
}
